import moment from 'moment';
import { useSelector } from 'react-redux';
import I18n from '../../../config/i18n';
import GoogleTagManagerNg from '../../../features/tracking/GoogleTagManagerNg';
import { Accommodation, Property } from '../../../routes/reducers/types';
import { RootState } from '../../../config/redux/types';
import { PriceOffer } from '../../../features/prices/types';
import { createAddToCartEvent } from '../../../features/tracking/eventCreators';

const dateStringFormat = 'YYYY-MM-DD';

/* eslint-disable camelcase */

interface BookingParams {
  accommodation_id: number;
  arrival: string;
  departure: string;
  num_persons: number;
}

interface SubmitButtonProps {
  booking?:
    | {
        vac: boolean;
        arr: boolean;
      }
    | number;
  isAbsolutePrice?: boolean;
  isPriceLoading?: boolean;
  accommodation: Pick<Accommodation, 'id' | 'display_title' | 'kind'>;
  property: Pick<Property, 'locality' | 'suite'>;
  arrival: moment.Moment;
  durationOfStay?: number;
  occupancy: number;
  matchingPrice: PriceOffer | null;
}

function getBookingParams(
  accommodation_id: number,
  arrival: moment.Moment | undefined,
  durationOfStay: number | undefined,
  occupancy: number,
): BookingParams | undefined {
  if (!arrival || !arrival.isValid() || !durationOfStay) {
    return undefined;
  }
  const departure = moment(arrival).add(durationOfStay, 'days');
  return {
    accommodation_id,
    arrival: moment(arrival).format(dateStringFormat),
    departure: moment(departure).format(dateStringFormat),
    num_persons: occupancy,
  };
}

export default function SubmitButton({
  booking,
  isAbsolutePrice,
  isPriceLoading,
  accommodation,
  property,
  arrival,
  durationOfStay,
  occupancy,
  matchingPrice,
}: SubmitButtonProps): JSX.Element {
  const locale = useSelector((state: RootState) => state.routes.locale);
  const bookingParams = getBookingParams(accommodation.id, arrival, durationOfStay, occupancy);
  const isDateBookable = booking && typeof booking !== 'number' && booking.vac && booking.arr;
  const isDisabled =
    (Array.isArray(bookingParams) && bookingParams.length === 0) ||
    !isDateBookable ||
    !isAbsolutePrice ||
    isPriceLoading;

  function handleSubmit(e: React.FormEvent<HTMLFormElement>): void {
    e.preventDefault();
    if (!bookingParams) {
      return;
    }

    const { event, ...eventData } = createAddToCartEvent(accommodation, property, matchingPrice);
    GoogleTagManagerNg.event(event, eventData);

    const searchParams = new URLSearchParams({
      accommodation_id: bookingParams.accommodation_id.toString(),
      arrival: bookingParams.arrival,
      departure: bookingParams.departure,
      num_persons: bookingParams.num_persons.toString(),
    });
    window.location.href = `/${locale}/booking/new?${searchParams.toString()}`;
  }

  return (
    <form method="POST" onSubmit={handleSubmit}>
      <button type="submit" className="availability-widget-booking-button" disabled={isDisabled}>
        {I18n.t('buttons.book_now')}
      </button>
    </form>
  );
}

const defaultProps: Pick<
  SubmitButtonProps,
  'booking' | 'isAbsolutePrice' | 'isPriceLoading' | 'durationOfStay'
> = {
  booking: undefined,
  isAbsolutePrice: false,
  isPriceLoading: false,
  durationOfStay: undefined,
};

SubmitButton.defaultProps = defaultProps;
